import { createContext, useContext } from 'react';

import type {
  ScopeAttentionFilterContext as ScopeAttentionFilterEvalContext,
  ScopeFilterKey,
} from '@/Client/Lib/scopeAttentionFilterLib';
import type { OverallTaskStatus } from '@kaiizen/kai-pro-api';

export type ScopeAttentionFilterContextValue = {
  activeFilters: ReadonlySet<ScopeFilterKey>;
  activeTaskStatus?: OverallTaskStatus;
  /** Evaluation inputs (e.g. contractor resolver) for context-dependent filters. */
  evalContext: ScopeAttentionFilterEvalContext;
  toggleFilter: (filter: ScopeFilterKey) => void;
  toggleTaskStatus: (status: OverallTaskStatus) => void;
  clearFilters: () => void;
};

export const ScopeAttentionFilterContext = createContext<ScopeAttentionFilterContextValue | null>(null);

export const useScopeAttentionFilters = (): ScopeAttentionFilterContextValue => {
  const context = useContext(ScopeAttentionFilterContext);
  if (!context) {
    throw new Error('useScopeAttentionFilters must be used within a ScopeAttentionFilterProvider');
  }
  return context;
};

/**
 * Non-throwing accessor for surfaces that render with or without the provider
 * (e.g. `ScopeNavigator`, which is reused in `CloseoutReviewFlow` where no
 * provider is mounted). Returns `null` when used outside a provider.
 */
export const useScopeAttentionFiltersOptional = (): ScopeAttentionFilterContextValue | null => useContext(ScopeAttentionFilterContext);
